import { SxProps, TextField, Theme, useTheme } from '@mui/material';
import React, { useState } from 'react';

interface CustomTextFieldProps {
  id: string;
  label?: string;
  value: string | number;
  onFieldChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  required?: boolean;
  customStyle?: SxProps<Theme>;
  error?: boolean;
  disabled?: boolean;
  helperText?: string;
  focusText?: string;
  onBlur?: () => void;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  placeholder?: string;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
}

const CustomTextField: React.FC<CustomTextFieldProps> = ({
  id,
  label,
  value,
  onFieldChange,
  customStyle,
  required = false,
  error = false,
  disabled = false,
  helperText = '',
  focusText = '',
  onBlur,
  startAdornment,
  endAdornment,
  placeholder,
  multiline = false,
  minRows,
  maxRows,
}) => {
  const theme = useTheme();
  const [focused, setFocused] = useState<boolean>(false);
  const inputStyle = {
    ...{
      '& .MuiInputLabel-root.Mui-focused': {
        color: theme.palette.primary.main,
      },
      '& .MuiInputBase-root': {
        color: '#162936',
        fontSize: '1rem',
        fontWeight: 500,
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: '#737F86',
        },
        '&.Mui-focused fieldset': {
          borderColor: theme.palette.primary.main,
        },
      },
    },
    ...customStyle,
  };

  return (
    <TextField
      id={id}
      {...(helperText ? { helperText } : {})}
      {...(focusText && focused ? { helperText: focusText } : {})}
      {...(onBlur
        ? {
            onBlur: () => {
              onBlur();
              setFocused(false);
            },
          }
        : { onBlur: () => setFocused(false) })}
      onFocus={() => setFocused(true)}
      required={required}
      {...(label ? { label } : {})}
      value={value}
      onChange={onFieldChange}
      disabled={disabled}
      error={error}
      multiline={multiline}
      {...(multiline ? { minRows, maxRows } : {})}
      InputProps={{
        ...(startAdornment && value ? { startAdornment } : {}),
        ...(endAdornment ? { endAdornment } : {}),
      }}
      InputLabelProps={{
        shrink: !!value || focused,
      }}
      sx={{
        ...inputStyle,
        '& .MuiFormHelperText-root': {
          margin: 0,
          padding: '0.25rem 0',
        },
      }}
      {...(placeholder ? { placeholder } : {})}
    />
  );
};

export default CustomTextField;
